import React from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import CombinedShapeMask from "@/components/CombinedShapeMask";
import { useTranslations } from "@/hooks/use-translations";
import { useLocale } from "@/components/locale-provider";

interface WaitlistConfirmationOverlayProps {
  email: string;
  onClose: () => void;
  onPlayVideo: () => void;
}

const WaitlistConfirmationOverlay: React.FC<
  WaitlistConfirmationOverlayProps
> = ({ email, onClose }) => {
  const { locale } = useLocale();
  const { t } = useTranslations(locale);

  return (
    <div className="fixed inset-0 bg-[#1c1815] bg-opacity-60 z-50 flex items-center justify-center">
      <div className="bg-[#fffcf5] w-full max-w-[1000px] rounded-[16px]   overflow-y-scroll mx-[24px] max-h-[95dvh]">
        <div className="flex flex-col md:grid md:grid-cols-2 w-full h-full overflow-hidden">
          {/* Left Column */}
          <div className="min-h-[300px] hidden md:block">
            <img
              src="/assets/women-sat-on-pier.jpg"
              alt="Women sat on a pier"
              className="w-full h-full object-cover"
            />
          </div>

          {/* Right Column */}
          <div className="px-[32px] pt-[64px] pb-[80px] relative h-full">
            <Button
              onClick={onClose}
              variant="ghost"
              size="icon"
              aria-label="Close"
              className="absolute top-3 right-3 z-10 text-white bg-[#C39D63] hover:bg-black/70 rounded-full"
            >
              <X size={24} />
            </Button>
            <div className="flex flex-col space-y-6 items-start justify-start w-full pb-[40px]">
              <div className="w-[124.59px] h-[100px]">
                <img
                  src="/assets/butterfly-logo.svg"
                  alt={t("common").logoAlt}
                  className=" object-contain"
                />
              </div>
              <div className="flex flex-col space-y-4 items-start justify-center w-full">
                <p className="text-6xl font-serifbold leading-[50px] text-[#c39d63] uppercase">
                  {t("waitlist").confirmation.almostThereTitle}
                </p>
                <div className="inline-flex items-center justify-center bg-[#f4eadf] bg-opacity-50 max-w-[282px] h-[42px] p-4">
                  <p className="text-sm font-sans tracking-[0.28px] leading-none text-center text-[#c39d63]">
                    {t("waitlist").confirmation.checkEmail} {email}!
                  </p>
                </div>
                <p className="text-sm font-sans tracking-[0.28px] leading-none text-[#1c1815]">
                  {t("waitlist").confirmation.excitementMessage}
                </p>
              </div>
            </div>
            <div className="flex flex-col space-y-4 items-start justify-start w-full">
              <p className="text-sm font-sans tracking-[0.28px] leading-none text-[#1c1815]">
                {t("waitlist").confirmation.inviteFriends}
              </p>
              <div className="inline-flex space-x-3 items-center justify-start mt-4">
                <Button 
                  size="icon" 
                  className="bg-[#573262] hover:bg-[#472251] w-8 h-8 p-1.5"
                  onClick={() => {
                    const shareText = t("social").confirmationShareText;
                    const shareUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
                    window.open(shareUrl, "_blank");
                  }}
                >
                  <img src="/assets/whatsapp.svg" alt="Share on WhatsApp" className="w-full h-full" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaitlistConfirmationOverlay;
